/**
 * "My Requests" tray (#419 closed-loop, requestor vantage) — the read-only mirror a
 * municipality / First Nation watches to track the assistance requests IT submitted,
 * WITHOUT phoning the PEOC. Scoped to this session's requests (session_id = getSessionId();
 * the demo requestor key, NOT a real auth boundary — cross-agency visibility stays
 * deferred to #190). Each row shows the REAL 7-state lifecycle (the unaltered status
 * machine), the requestor read-backs carried on the LIST (incident #, down-tasks, the
 * bound resource), and — crucially — the PEOC receipt as a DISCRETE "Received by PEOC ·
 * <time>" badge rendered OFF the status stepper: an optional, orthogonal receipt spliced
 * inline into the ordered stages would read as advancement (dishonest), and the
 * ack-skipped→triaged case would read as a missing step (also dishonest).
 *
 * Read-only from the requestor vantage — NO advance / forward / cancel here; those are
 * the PEOC's controls in AssistanceRequestDetail. Two-way coordination is the named-next
 * #420 (see the muted forward-pointing line below); this builds NO conversation surface.
 *
 * Polled on the shared 30s cadence + the assistBridge nudge (so a just-submitted request
 * appears at once). Reports its OWN open count for the per-tab badge only; it is
 * deliberately NOT summed into the aggregate Coordination badge (those same rows are the
 * PEOC's Assist worklist — summing both would double-count the requestor's mirror).
 */
import { useCallback, useEffect, useState } from 'react';
import type { AssistanceRequestListItem } from '@tsr/shared-types';
import { fetchAssistanceRequests } from './api';
import {
  ASSIST_STATUS_LABEL,
  ASSIST_STATUS_DOT,
  ASSIST_URGENCY_TONE,
  openAssistCount,
  sortAssistRows,
} from './AssistQueueList';
import { ASSIST_POLL_MS, buildAssistListSteps } from './AssistQueuePanel';
import { LifecycleStepper, type StepperUrgency } from './components/LifecycleStepper';
import { InteropsThreadPanel } from './InteropsThreadPanel';
import { useAssistRefresh } from './assistBridge';
import { getSessionId } from './lib/operatorActions';

/** Compact UTC stamp for the receipt badge (no seconds). */
function fmtReceived(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return iso.length >= 16 ? `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC` : iso;
}

/** The requestor read-backs (incident #, down-tasks, bound resource), folded into one
 *  muted line. Returns null when the request carries none of them yet. */
function readBacks(r: AssistanceRequestListItem): string | null {
  const parts: string[] = [];
  if (r.incident_number) parts.push(r.incident_number);
  if (r.down_tasks && r.down_tasks.length > 0) {
    parts.push(`${r.down_tasks.length} agency task${r.down_tasks.length === 1 ? '' : 's'}`);
  }
  if (r.assigned_resource_request) {
    parts.push(
      `${r.assigned_resource_request.request_number} · ${r.assigned_resource_request.request_status.replace(/_/g, ' ')}`,
    );
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function MyAssistRequestsPanel({
  onActiveCountChange,
}: {
  /** Report the count of OPEN (non-terminal) requests up for the sub-tab badge. */
  onActiveCountChange?: (count: number) => void;
}): JSX.Element {
  const [rows, setRows] = useState<AssistanceRequestListItem[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setRefreshKey((k) => k + 1), ASSIST_POLL_MS);
    return () => clearInterval(id);
  }, []);

  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);
  useAssistRefresh(bump);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchAssistanceRequests({ session_id: getSessionId(), limit: 50 }, ctl.signal)
      .then((r) => setRows(sortAssistRows(r.requests)))
      .catch(() => undefined);
    return () => ctl.abort();
  }, [refreshKey]);

  useEffect(() => {
    onActiveCountChange?.(openAssistCount(rows));
  }, [rows, onActiveCountChange]);

  return (
    <div className="px-3 py-3" data-testid="my-requests-panel">
      <p className="mb-2 text-[11px] leading-relaxed text-slate-400">
        The assistance requests you submitted, with their live status — track each one move
        from submitted through to resourced without phoning. Read-only; updates automatically.
      </p>
      {rows.length === 0 ? (
        <p className="text-[12px] text-slate-500" data-testid="my-requests-empty">
          You haven&rsquo;t submitted any assistance requests yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => {
            const received = fmtReceived(r.acknowledged_at);
            const backs = readBacks(r);
            return (
              <li
                key={r.id}
                data-testid="my-request-row"
                className="rounded border border-slate-700 bg-slate-800/40 px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${ASSIST_STATUS_DOT[r.status] ?? 'bg-slate-500'}`}
                    title={r.status}
                  />
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-slate-100">
                    {r.requestor_name}
                  </span>
                  <span
                    className={`shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${ASSIST_URGENCY_TONE[r.urgency]}`}
                  >
                    {r.urgency}
                  </span>
                  {r.response_level != null && (
                    <span className="shrink-0 rounded bg-slate-700 px-1.5 py-0.5 text-[10px] font-semibold text-slate-200">
                      L{r.response_level}
                    </span>
                  )}
                </div>

                {/* The CURRENT real status, in plain words (a REAL label — never an
                    invented "en route"; the machine is unaltered). */}
                <p className="mt-1 pl-4 text-[11px] text-slate-300">
                  Status: <span className="text-slate-100">{ASSIST_STATUS_LABEL[r.status]}</span>
                </p>

                {/* The PEOC receipt — a DISCRETE badge, OFF the stepper (orthogonal to
                    status; absence means "not yet acknowledged", never a skipped stage). */}
                {received && (
                  <p
                    data-testid="my-request-received-badge"
                    className="mt-1 pl-4 text-[10px] uppercase tracking-wide text-emerald-300"
                  >
                    ✓ Received by PEOC · {received}
                  </p>
                )}

                {backs && (
                  <p
                    data-testid="my-request-readbacks"
                    className="mt-1 truncate pl-4 text-[11px] text-slate-400"
                    title={backs}
                  >
                    {backs}
                  </p>
                )}

                <div className="mt-2">
                  <LifecycleStepper
                    data-testid="my-request-stepper"
                    steps={buildAssistListSteps(r.status)}
                    urgency={r.urgency as StepperUrgency}
                  />
                  {r.status === 'cancelled' && (
                    <p className="mt-1 text-[10px] uppercase tracking-wide text-rose-300">Cancelled</p>
                  )}
                </div>

                {/* RBAC Slice 3c (#572) + #451 — the PEOC's onward handling of THIS request
                    (recorded onward legs + recorded responses), READ-ONLY, PLUS the originator's
                    constrained reply composer (#451): reply to the PEOC on this request without
                    phoning. Renders nothing until there is handling to show; the reply composer is
                    dormant under the AUTH-off single-seat demo (the originator + PEOC collapse to
                    one agency — #551) and activates with the enforced seat identity (#190). */}
                <div className="mt-2" data-testid="my-request-onward-handling">
                  <InteropsThreadPanel
                    composerMode="reply"
                    contextType="assistance_request"
                    contextId={r.id}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
